import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Textarea,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Text,
  Icon,
  FormHelperText,
  Select,
} from "@chakra-ui/react";
import { WarningIcon } from "@chakra-ui/icons";

const LostModal = ({ isOpen, onClose, place, onUpdate }) => {
  const [formData, setFormData] = useState({
    status: "lost",
    lost_reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.lost_reason.trim()) {
      setError("Motivo da perda é obrigatório");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Garantir que os tipos estejam corretos antes de enviar
      const cleanFormData = {
        ...formData,
        lost_reason: formData.lost_reason ? String(formData.lost_reason).trim() : "",
      };

      await onUpdate(cleanFormData);
      handleClose();
    } catch (error) {
      setError("Erro ao registrar perda");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        status: "lost",
        lost_reason: "",
      });
      setError(null);
      onClose();
    }
  };

  const commonLostReasons = [
    "",
    "Cliente já possui fornecedor",
    "Preço muito alto",
    "Não tem interesse no momento",
    "Decidiu não prosseguir",
    "Não conseguiu contato",
    "Empresa fechada/inativa",
    "Não é o público-alvo",
    "Produto/serviço não atende necessidade",
    "Questões orçamentárias",
    "Timing inadequado",
    "Concorrente ofereceu melhor proposta",
    "Processo interno muito demorado",
    "Mudança de prioridades do cliente",
    "Outro motivo"
  ];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={WarningIcon} color="red.500" />
            <Text>Registrar como Perdido</Text>
          </HStack>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {place?.title}
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={loading} />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text fontWeight="semibold" fontSize="sm">
                  📊 Importante para análise
                </Text>
                <AlertDescription fontSize="sm">
                  Registrar o motivo da perda ajuda a melhorar a estratégia
                  de prospecção e identificar padrões.
                </AlertDescription>
              </VStack>
            </Alert>

            <FormControl>
              <FormLabel>Motivo Rápido</FormLabel>
              <Select
                placeholder="Selecione um motivo comum (opcional)"
                value={formData.lost_reason}
                onChange={(e) => handleInputChange("lost_reason", e.target.value)}
                isDisabled={loading}
              >
                {commonLostReasons.slice(1).map((reason, index) => (
                  <option key={index} value={reason}>
                    {reason}
                  </option>
                ))}
              </Select>
              <FormHelperText>
                Escolha um motivo comum ou escreva personalizado abaixo
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Motivo da Perda</FormLabel>
              <Textarea
                placeholder="Descreva detalhadamente por que este prospect foi perdido..."
                value={formData.lost_reason}
                onChange={(e) => handleInputChange("lost_reason", e.target.value)}
                isDisabled={loading}
                rows={4}
              />
              <FormHelperText>
                Seja específico para ajudar na análise futura
              </FormHelperText>
            </FormControl>

            <Alert status="warning" borderRadius="md" size="sm">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                <strong>Dica:</strong> Prospects perdidos podem ser revisitados
                em campanhas futuras. Mantenha registros detalhados para
                possível reativação posterior.
              </AlertDescription>
            </Alert>

            <Alert status="info" borderRadius="md" size="sm">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                <strong>Para análise, considere incluir:</strong>
                <br />
                • Fase em que foi perdido
                <br />
                • Objeções principais
                <br />
                • Concorrentes mencionados
                <br />
                • Questões de timing/orçamento
              </AlertDescription>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              onClick={handleClose}
              isDisabled={loading}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="red"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Registrando..."
              leftIcon={<WarningIcon />}
            >
              Registrar como Perdido
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LostModal;